'use client';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useEffect, useRef } from 'react';

const CONTENT = {
  sk: {
    lead: (
      <>
        Rodiny z celého Slovenska, ktoré prešli na{' '}
        <strong style={{ color: 'hsl(90 57% 75%)' }}>koreňovú čističku odpadových vôd</strong>.
      </>
    ),
    prevAriaLabel: 'Predchádzajúca recenzia',
    nextAriaLabel: 'Ďalšia recenzia',
  },
  de: {
    lead: (
      <>
        Familien aus der ganzen Slowakei, die auf eine{' '}
        <strong style={{ color: 'hsl(90 57% 75%)' }}>Pflanzenkläranlage</strong> umgestiegen sind.
      </>
    ),
    prevAriaLabel: 'Vorherige Bewertung',
    nextAriaLabel: 'Nächste Bewertung',
  },
};

const TIME_TRANSLATIONS: Record<string, string> = {
  'pred 10 mesiacmi': 'vor 10 Monaten',
  'pred 2 rokmi': 'vor 2 Jahren',
  'pred rokom': 'vor einem Jahr',
  'Google · Miestny sprievodca · pred rokom': 'Google · Lokaler Guide · vor einem Jahr',
};

const GoogleBadge = () => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      background: '#fff',
      border: '1px solid hsl(240 4% 91%)',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 600,
      color: 'hsl(245 6% 45%)',
    }}
  >
    <span style={{ color: '#4285F4', fontWeight: 900 }}>G</span>
    <span style={{ color: '#EA4335', fontWeight: 900 }}>o</span>
    <span style={{ color: '#FBBC04', fontWeight: 900 }}>o</span>
    <span style={{ color: '#4285F4', fontWeight: 900 }}>g</span>
    <span style={{ color: '#34A853', fontWeight: 900 }}>l</span>
    <span style={{ color: '#EA4335', fontWeight: 900 }}>e</span>
  </div>
);

const reviews = [
  {
    initials: 'JK',
    name: 'Ján Kalamenovič',
    time: 'pred 10 mesiacmi',
    text: {
      sk: '„Môžem odporučiť. Po roku užívania sme spokojní. Pri samoosadení čističky komunikácia cez telefón, vždy ochotní odpovedať na moje otázky."',
      de: '„Ich kann es empfehlen. Nach einem Jahr Nutzung sind wir zufrieden. Bei der Selbstmontage der Anlage war die telefonische Kommunikation stets hilfsbereit bei der Beantwortung meiner Fragen."',
    },
  },
  {
    initials: 'JK',
    name: 'Jan Kurtik',
    time: 'pred 2 rokmi',
    text: {
      sk: '„S firmou Ekočističky u nás maximálna spokojnosť. Od prvého kontaktu, kedy som len potreboval poradiť a predpripraviť stavbu, bola komunikácia s pánom Krakovským na výbornej úrovni. Všetko následne perfektne zorganizované a partia, ktorá u nás bola, odviedla perfektnú prácu. Bodaj by bolo na Slovensku viac takýchto firiem."',
      de: '„Mit der Firma Ekočističky sind wir maximal zufrieden. Vom ersten Kontakt an, als ich nur Beratung brauchte und den Bau vorbereiten wollte, war die Kommunikation mit Herrn Krakovský ausgezeichnet. Alles war anschließend perfekt organisiert, und das Team, das bei uns war, hat hervorragende Arbeit geleistet. Gäbe es doch mehr solcher Firmen in der Slowakei."',
    },
  },
  {
    initials: 'MK',
    name: 'Marko Komoráš',
    time: 'pred rokom',
    text: {
      sk: '„Od prvej komunikácie až po realizáciu veľmi dobrá spokojnosť. Vybrali sme si produkt nádrž na zachytávanie dažďovej vody a dažďovú záhradu. Páni majstri šikovní, od rána v pohybe a skončia až keď dokončia — žiadne flákanie. Tak isto široký sortiment strojov. Môžem len odporučiť."',
      de: '„Von der ersten Kommunikation bis zur Umsetzung waren wir sehr zufrieden. Wir haben uns für eine Regenwasserzisterne und einen Regengarten entschieden. Die Handwerker waren geschickt, von morgens bis zur Fertigstellung im Einsatz — kein Herumtrödeln. Auch die Auswahl an Geräten war groß. Ich kann es nur empfehlen."',
    },
  },
  {
    initials: 'IL',
    name: 'Ivan Lukáč',
    time: 'Google · Miestny sprievodca · pred rokom',
    text: {
      sk: '„Realizácia koreňovej ČOV v obci Beluj pri Banskej Štiavnici. Výborná komunikácia s pánom Krakovským, napriek špecifickým podmienkam realizácia veľmi rýchla a bezproblémová. Odporúčam všetkými desiatimi."',
      de: '„Umsetzung einer Pflanzenkläranlage in der Gemeinde Beluj bei Banská Štiavnica. Ausgezeichnete Kommunikation mit Herrn Krakovský, trotz besonderer Bedingungen verlief die Umsetzung sehr schnell und problemlos. Ich empfehle es uneingeschränkt."',
    },
  },
  {
    initials: 'IK',
    name: 'Ignis Krby',
    time: 'pred 2 rokmi',
    text: {
      sk: '„S prácou pána Krakovského, ako aj s montážnikmi, sme spokojní. Snažili sa, v čom mohli, nám vyšli v ústrety. Zatiaľ nebývame, ale keď treba — vždy zdvihne telefón a obratom vyrieši. Prajeme mu pevné zdravie a úspech v podnikaní."',
      de: '„Mit der Arbeit von Herrn Krakovský sowie den Monteuren sind wir zufrieden. Sie haben sich bemüht, uns entgegenzukommen, wo immer es möglich war. Wir wohnen noch nicht dort, aber wenn nötig, geht er immer ans Telefon und löst es umgehend. Wir wünschen ihm gute Gesundheit und Erfolg im Geschäft."',
    },
  },
  {
    initials: 'JK',
    name: 'Jozife K.',
    time: 'pred rokom',
    text: {
      sk: '„Komunikácia bola super a aj vysvetlenie procesu výstavby a odporučenie vhodného riešenia pre nás. Dohodnutý termín realizácie bol splnený a to chlapi robili aj za počasia, ktoré nebolo príliš príjemné (dážď, blato, zima). Dielo funguje bez problémov. Odporúčam."',
      de: '„Die Kommunikation war super, ebenso die Erklärung des Bauprozesses und die Empfehlung der passenden Lösung für uns. Der vereinbarte Umsetzungstermin wurde eingehalten, und die Jungs haben auch bei unangenehmem Wetter gearbeitet (Regen, Matsch, Kälte). Die Anlage funktioniert problemlos. Ich empfehle es."',
    },
  },
  {
    initials: 'BJ',
    name: 'Barbora Janošková',
    time: 'pred rokom',
    text: {
      sk: '„Firma Ekočističky pozitívne prekvapila vo všetkých ohľadoch — od prvotného kontaktu, cez komunikáciu, realizáciu, až po postoj ku komplikáciám. Vyzdvihla by som najmä aktívny a pozitívny prístup k riešeniu problémov. Reagujú naozaj promptne a príjemne. Vždy, keď boli u nás, všetko prebehlo podľa dohody."',
      de: '„Die Firma Ekočističky hat in jeder Hinsicht positiv überrascht — vom ersten Kontakt über die Kommunikation und Umsetzung bis hin zum Umgang mit Komplikationen. Besonders hervorheben möchte ich den aktiven und positiven Umgang mit Problemen. Sie reagieren wirklich prompt und freundlich. Immer, wenn sie bei uns waren, verlief alles wie vereinbart."',
    },
  },
  {
    initials: 'JC',
    name: 'Jaroslav Chlebo Sr.',
    time: 'pred 2 rokmi',
    text: {
      sk: '„Zatiaľ môžem hodnotiť iba realizačnú časť stavby koreňovej čističky. Každopádne prístup spoločnosti a jej pracovníkov k mojej zákazke bol ukážkový, termíny a plnenia boli v plnom rozsahu dodržané. Dúfam, že také budú aj moje skúsenosti s užívaním. Prajem ďalšie úspechy."',
      de: '„Bisher kann ich nur den Umsetzungsteil des Baus der Pflanzenkläranlage bewerten. Auf jeden Fall war der Umgang der Firma und ihrer Mitarbeiter mit meinem Auftrag vorbildlich, Termine und Leistungen wurden vollständig eingehalten. Ich hoffe, dass auch meine Erfahrungen bei der Nutzung so ausfallen werden. Ich wünsche weiterhin viel Erfolg."',
    },
  },
  {
    initials: 'V',
    name: 'Volvo',
    time: 'pred 2 rokmi',
    text: {
      sk: '„Ďakujeme veľmi pekne za montáž nádrže na dažďovú vodu. Chlapci sú naozaj veľmi šikovní a profesionálni. Komunikácia, ako aj samotná realizácia, bola na vysokej úrovni. Ďakujeme."',
      de: '„Vielen Dank für die Montage der Regenwasserzisterne. Die Jungs sind wirklich sehr geschickt und professionell. Sowohl die Kommunikation als auch die Umsetzung selbst waren auf hohem Niveau. Danke."',
    },
  },
  {
    initials: 'MS',
    name: 'Martin Sýkora',
    time: 'pred rokom',
    text: {
      sk: '„Veľmi pozitívna skúsenosť s firmou, veľmi dobrá komunikácia a profesionálny prístup. Koreňovú ČOV máme už 3 roky a sme maximálne spokojní."',
      de: '„Sehr positive Erfahrung mit der Firma, sehr gute Kommunikation und professionelles Vorgehen. Wir haben die Pflanzenkläranlage schon seit 3 Jahren und sind maximal zufrieden."',
    },
  },
];

export function Reviews() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const content = locale === 'de' ? CONTENT.de : CONTENT.sk;
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.children;
    let raf: number | null = null;

    function update() {
      raf = null;
      const trackRect = track!.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i] as HTMLElement;
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const distance = Math.abs(cardCenter - center);
        const maxDistance = trackRect.width / 2;
        const ratio = Math.min(distance / maxDistance, 1);
        const scale = 1 - ratio * 0.45;
        const opacity = 1 - ratio * 0.6;
        card.style.transform = `scale(${scale.toFixed(3)})`;
        card.style.opacity = opacity.toFixed(3);
      }
    }

    function schedule() {
      if (!raf) raf = requestAnimationFrame(update);
    }

    track.scrollLeft = 3 * (280 + 20);

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const t1 = setTimeout(update, 50);
    const t2 = setTimeout(update, 300);

    return () => {
      track.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <section style={{ background: 'hsl(90 40% 18%)', padding: 'clamp(48px,8vw,80px) 0' }}>
      <style>{`
        #testimonial-track::-webkit-scrollbar { display: none; }
        #testimonial-track { scrollbar-width: none; -ms-overflow-style: none; }
        #testimonial-track > div { transition: transform 0.35s cubic-bezier(0.22,0.61,0.36,1), opacity 0.35s ease; transform-origin: center center; will-change: transform, opacity; }
      `}</style>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <p
            style={{
              fontSize: '14px',
              fontWeight: 700,
              color: 'hsl(90 57% 65%)',
              marginBottom: '8px',
            }}
          >
            {t('text3944')}
          </p>
          <h2
            style={{
              fontSize: 'clamp(1.5rem,3.5vw,2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'white',
              marginBottom: '12px',
            }}
          >
            {t('text3945')}
          </h2>
          <p
            style={{
              fontSize: 'clamp(0.9rem,1.6vw,1.05rem)',
              color: 'hsl(90 20% 70%)',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            {content.lead}
          </p>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <button
          aria-label={content.prevAriaLabel}
          onClick={() => trackRef.current?.scrollBy({ left: -300, behavior: 'smooth' })}
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 5,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'hsl(90 57% 35%)',
            fontSize: '24px',
            fontWeight: 700,
          }}
        >
          &#10094;
        </button>
        <button
          aria-label={content.nextAriaLabel}
          onClick={() => trackRef.current?.scrollBy({ left: 300, behavior: 'smooth' })}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 5,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'hsl(90 57% 35%)',
            fontSize: '24px',
            fontWeight: 700,
          }}
        >
          &#10095;
        </button>
        <div style={{ overflow: 'hidden' }}>
          <div
            id="testimonial-track"
            ref={trackRef}
            style={
              {
                display: 'flex',
                gap: '20px',
                overflowX: 'auto',
                padding: '20px calc(50vw - 140px)',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
              } as React.CSSProperties
            }
          >
            {reviews.map((r, i) => (
              <div
                key={i}
                style={{
                  minWidth: '260px',
                  maxWidth: '280px',
                  background: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  flexShrink: 0,
                  scrollSnapAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ color: '#FADB14', fontSize: '14px', letterSpacing: '2px' }}>
                    ★★★★★
                  </div>
                  <GoogleBadge />
                </div>
                <p
                  style={{
                    fontSize: '15px',
                    color: 'hsl(240 10% 10%)',
                    lineHeight: 1.75,
                    fontStyle: 'italic',
                    flex: 1,
                    marginBottom: '20px',
                  }}
                >
                  {locale === 'de' ? r.text.de : r.text.sk}
                </p>
                <div
                  style={{
                    borderTop: '1px solid hsl(240 4% 91%)',
                    paddingTop: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: 'hsl(90 57% 93%)',
                      color: 'hsl(90 57% 35%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '15px',
                    }}
                  >
                    {r.initials}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>{r.name}</div>
                    <div style={{ fontSize: '12px', color: 'hsl(245 6% 45%)' }}>
                      {t('text3946')} · {locale === 'de' ? (TIME_TRANSLATIONS[r.time] ?? r.time) : r.time}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '24px', padding: '0 24px' }}>
        <p style={{ fontSize: '13px', color: 'hsl(90 20% 55%)' }}>
          {t('text3947')}
        </p>
      </div>
    </section>
  );
}
